/* eslint-disable react/prop-types */
import React from "react";
import { Box, Typography, Chip, Tooltip, makeStyles } from "@material-ui/core";
import _ from "lodash";
import { AiOutlineWarning } from "react-icons/ai";
import computeScenarioTotalsForCard from "lib/utils/scenarios";
import { FaUser } from "react-icons/fa";
import ScenarioTotalsCardComponent from "../ScenarioTotalsCardComponent";

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(1.5),
  },
  constraintContainer: {
    boxShadow: "0 1px 4px 0 rgba(37, 50, 56, 0.1)",
    borderRadius: "3px",
    marginBottom: "5px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
  },
  inputTitleBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "5px",
  },
  titleAndRestrictionBox: {
    display: "flex",
    justifyContent: "space-between",
  },
  alertIcon: {
    paddingLeft: "10px",
    color: theme.palette.primary.main,
    lineHeight: "10px",
  },
  chosenGridTitle: {
    marginBottom: "5px",
  },
  gridOwnerContainer: {
    display: "flex",
    alignItems: "center",
    "& > *:first-child": {
      marginRight: "5px",
    },
  },
  gridOwner: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "135px",
  },
  gridInfoContainer: {
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.up("xs")]: {
      flexDirection: "column",
    },
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
    },
    "& > :last-child": {
      padding: "5px 15px 5px 15px",
      backgroundColor: "#f0f0f0",
      display: "flex",
      [theme.breakpoints.up("xs")]: {
        flexDirection: "row",
        flexWrap: "wrap",
      },
      [theme.breakpoints.up("sm")]: {
        flexDirection: "column",
        flexWrap: "nowrap",
      },
      justifyContent: "space-evenly",
    },
  },
  gridBoxLeft: {
    paddingBottom: "10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexGrow: "1",
    "& >:first-child": {
      display: "flex",
      flexDirection: "column",
      alignItems: "initial",
    },
  },
  chipBox: {
    marginLeft: "10px",
    marginRight: "10px",
  },
  chip: {
    margin: "3px",
    fontSize: "11px",
  },
}));

const ChosenGridComponent = ({
  scenarioMapSelectedScenario,
  scenarioMapReferenceScenario,
  filters,
  constraints,
  metadata,
}) => {
  const classes = useStyles();

  const isScenarioMapSelectedScenarioRestricted = () => {
    if (
      scenarioMapSelectedScenario.min_price_change * 100 <
        constraints.priceRange[0] ||
      scenarioMapSelectedScenario.max_price_change * 100 >
        constraints.priceRange[1]
    )
      return true;
    if (constraints.totalInvestmentIn) {
      if (
        scenarioMapSelectedScenario.investment_in -
          scenarioMapReferenceScenario.investment_in >
        constraints.totalInvestmentIn
      )
        return true;
    } else if (
      scenarioMapSelectedScenario.investment_in -
        scenarioMapReferenceScenario.investment_in >
      constraints.totalInvestment
    )
      return true;

    const pctUnits =
      100 *
      ((scenarioMapSelectedScenario.units -
        scenarioMapReferenceScenario.units) /
        scenarioMapReferenceScenario.units);

    if (
      pctUnits < constraints.unitsRange[0] ||
      pctUnits > constraints.unitsRange[1]
    )
      return true;

    const pctMargin =
      100 *
      ((scenarioMapSelectedScenario.margin -
        scenarioMapReferenceScenario.margin) /
        scenarioMapReferenceScenario.margin);

    if (
      pctMargin < constraints.marginRange[0] ||
      pctMargin > constraints.marginRange[1]
    )
      return true;
    // if (
    //   scenarioMapSelectedScenario.scenario.samePriceEveryday !==
    //   constraints.samePriceToggle
    // )
    //   return true;

    return false;
  };

  return (
    <Box component="div" className={classes.constraintContainer}>
      {_.isEmpty(scenarioMapSelectedScenario) ? (
        <Box
          component="div"
          className={`${classes.inputTitleBox} ${classes.padding}`}
        >
          <Typography variant="body2">
            Click on a point in the map to select a scenario.
          </Typography>
        </Box>
      ) : (
        <>
          <Box component="div" className={classes.gridInfoContainer}>
            <Box component="div" className={classes.gridBoxLeft}>
              <Box
                component="div"
                className={`${classes.inputTitleBox} ${classes.padding}`}
              >
                <Box component="div" className={classes.titleAndRestrictionBox}>
                  <Typography
                    variant="body2"
                    className={classes.chosenGridTitle}
                  >
                    <b>Chosen promotional grid</b>
                  </Typography>
                  {isScenarioMapSelectedScenarioRestricted() ? (
                    <Tooltip
                      title="This grid violates at least one restriction."
                      placement="left"
                    >
                      <Box component="span" className={classes.alertIcon}>
                        <AiOutlineWarning size={20} />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Box>
                <Box className={classes.gridOwnerContainer}>
                  <FaUser />
                  <Tooltip
                    title={scenarioMapSelectedScenario.scenario.owner_label}
                    placement="right"
                  >
                    <Typography variant="body2" className={classes.gridOwner}>
                      <i>{scenarioMapSelectedScenario.scenario.owner_label}</i>
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
              <Box component="div" className={classes.chipBox}>
                {_.some(filters, (filter) => filter !== "") ? (
                  _.map(filters, (value, key) => {
                    return value ? (
                      <Chip
                        variant="outlined"
                        size="small"
                        key={key}
                        label={value.label}
                        className={classes.chip}
                      />
                    ) : null;
                  })
                ) : (
                  <Typography variant="body2">No filters applied.</Typography>
                )}
              </Box>
            </Box>
            <ScenarioTotalsCardComponent
              isReference={
                scenarioMapSelectedScenario.scenario.scenario_type_id === 1
              }
              cardData={computeScenarioTotalsForCard(
                scenarioMapSelectedScenario,
                scenarioMapReferenceScenario
              )}
              visibleKeys={["units", "margin", "sales"]}
              metadata={metadata}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ChosenGridComponent;
